/**
 * Сделка «Фибо-пульта» поверх сетки.
 *
 * Как у прогноза позиции: в правой части сетки — полоса прибыли до цели
 * и полоса риска до стопа, линии входа, стопа и цели с подписями у левого
 * края полосы (справа от сетки — подписи её уровней, туда не лезем). План
 * считает `fiboPlan` из `@specter/types` — та же функция, по которой
 * сервер ставит заявку, поэтому на графике ровно те цены, что уйдут на
 * площадку (на счёте другой биржи — с поправкой на её цену).
 *
 * Пока сделка не запущена (окно открыто, `enabled: false`), всё рисуется
 * бледнее и пунктиром — предпросмотр: видно, куда встанет заявка, ещё до
 * кнопки.
 */

import { fiboPlan } from '@specter/types';
import { withAlpha } from './color.js';
import type { Drawing } from './types.js';

interface Screen {
  readonly x: number;
  readonly y: number;
}

interface Scope {
  readonly ctx: CanvasRenderingContext2D;
  readonly width: number;
  readonly screen: (point: { time: number; price: number }) => Screen | null;
}

const UP = '#2fe6a6';
const DOWN = '#ff4d7a';
const ENTRY = '#eef0ff';
const TRIGGER = '#4fe3ff';
/** Ширина полосы сделки: доля ширины сетки, но не уже и не шире этого. */
const BOX = { share: 0.55, min: 190, max: 320 } as const;

/** Подпись доли: 1 и 2 — с нулём после точки, как на кнопках окна. */
export function levelText(value: number): string {
  return Number.isInteger(value) ? value.toFixed(1) : String(value);
}

function price(value: number): string {
  const abs = Math.abs(value);
  const digits = abs >= 1000 ? 1 : abs >= 1 ? 2 : abs >= 0.01 ? 4 : 8;
  return value.toFixed(digits);
}

export function drawFiboTrade(scope: Scope, shape: Drawing, a: Screen, b: Screen): void {
  const trade = shape.trade;
  if (!trade) return;
  const plan = fiboPlan(shape, trade);
  if (!plan) return;
  const { ctx } = scope;
  const at = (value: number): number | null => scope.screen({ time: shape.points[0]!.time, price: value })?.y ?? null;
  const entryY = at(plan.averageEntry);
  const stopY = at(plan.stop);
  const targetYs = plan.targets.map((target) => at(target.price));
  if (entryY === null || stopY === null || targetYs.some((y) => y === null)) return;

  const right = Math.min(scope.width, Math.max(a.x, b.x));
  const span = Math.abs(b.x - a.x) * BOX.share;
  const wide = Math.max(0, right - Math.min(BOX.max, Math.max(BOX.min, span)));
  // Не от самой опоры сетки: у разворота цель 1.0 — ровно точка, где нажали, и подпись легла бы на кольцо.
  // Сетка совсем узкая — полоса выходит за её левый край как есть.
  const clear = Math.min(a.x, b.x) + 16;
  const left = right - clear >= 120 ? Math.max(wide, clear) : wide;
  const preview = !trade.enabled;
  const long = plan.side === 'LONG';
  const sideWord = long ? 'лонг' : 'шорт';

  ctx.save();
  if (preview) ctx.globalAlpha *= 0.6;

  // Полосы: прибыль — до дальней цели, риск — до стопа.
  const farY = targetYs.at(-1)!;
  band(ctx, left, right, entryY, farY, UP);
  band(ctx, left, right, entryY, stopY, DOWN);

  const dash = preview ? [5, 4] : [];
  const pills: Array<{ y: number; text: string; color: string; fill: string }> = [];

  if (plan.trigger !== null && !plan.market) {
    const y = at(plan.trigger);
    if (y !== null) {
      stroke(ctx, left, right, y, TRIGGER, 1, [3, 4]);
      pills.push({ y, text: `Пробой · ${price(plan.trigger)}`, color: TRIGGER, fill: 'rgba(8, 24, 34, 0.92)' });
    }
  }
  plan.entries.forEach((entry, index) => {
    const y = at(entry.price);
    if (y === null) return;
    stroke(ctx, left, right, y, ENTRY, 1.5, dash);
    let text: string;
    if (trade.mode === 'trend') text = plan.market ? `Вход на пробое · ${price(entry.price)} · ${sideWord}` : `Вход на откате 1.0 · ${price(entry.price)} · ${sideWord}`;
    else if (plan.entries.length > 1) text = `½ на ${levelText(index === 0 ? trade.entry : trade.entry2!)} · ${price(entry.price)}${index === 0 ? ` · ${sideWord}` : ''}`;
    else text = `Лимитка ${levelText(trade.entry)} · ${price(entry.price)} · ${sideWord}`;
    pills.push({ y, text, color: ENTRY, fill: 'rgba(17, 20, 46, 0.92)' });
  });
  stroke(ctx, left, right, stopY, DOWN, 1.5, dash);
  pills.push({ y: stopY, text: `Стоп ${levelText(trade.stop)} · ${price(plan.stop)}`, color: '#ff9ab4', fill: 'rgba(42, 13, 24, 0.92)' });
  plan.targets.forEach((target, index) => {
    const y = targetYs[index]!;
    stroke(ctx, left, right, y, UP, 1.5, dash);
    const r = `+${plan.rewardR.toFixed(1).replace('.', ',')}R`;
    const rest = trade.mode === 'trend' ? 1.618 : 0.5;
    const text = index === 0 ? `Цель ${levelText(trade.target)} · ${price(target.price)} · ${r}` : `Остаток до ${levelText(rest)} · ${price(target.price)}`;
    pills.push({ y, text, color: UP, fill: 'rgba(11, 29, 22, 0.92)' });
  });

  // Подписи — у левого края полосы, не налезая друг на друга.
  pills.sort((p, q) => p.y - q.y);
  let previous = -Infinity;
  for (const item of pills) {
    const y = Math.max(item.y, previous + 20);
    pill(ctx, left + 6, y, item.text, item.color, item.fill);
    previous = y;
  }
  // Пометка предпросмотра — под нижней линией: сверху сетка со своей золотой зоной.
  if (preview) pill(ctx, left + 6, previous + 22, 'предпросмотр — ещё не запущено', '#a9afd6', 'rgba(17, 20, 46, 0.92)');
  ctx.restore();
}

function band(ctx: CanvasRenderingContext2D, left: number, right: number, from: number, to: number, color: string): void {
  const top = Math.min(from, to);
  const height = Math.abs(to - from);
  if (height < 1) return;
  const gradient = ctx.createLinearGradient(left, 0, right, 0);
  gradient.addColorStop(0, withAlpha(color, 0.2));
  gradient.addColorStop(1, withAlpha(color, 0.05));
  ctx.fillStyle = gradient;
  ctx.fillRect(left, top, right - left, height);
}

function stroke(ctx: CanvasRenderingContext2D, left: number, right: number, y: number, color: string, width: number, dash: number[]): void {
  ctx.save();
  ctx.setLineDash(dash);
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.beginPath();
  ctx.moveTo(left, Math.round(y) + 0.5);
  ctx.lineTo(right, Math.round(y) + 0.5);
  ctx.stroke();
  ctx.restore();
}

function pill(ctx: CanvasRenderingContext2D, x: number, y: number, text: string, color: string, fill: string, align: 'left' | 'right' = 'left'): void {
  ctx.save();
  ctx.setLineDash([]);
  ctx.font = '700 11.5px Onest, system-ui, sans-serif';
  const width = ctx.measureText(text).width + 16;
  const height = 20;
  const left = align === 'right' ? x - width : x;
  ctx.beginPath();
  ctx.roundRect(left, y - height / 2, width, height, 8);
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.strokeStyle = withAlpha(color, 0.8);
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.fillStyle = color;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, left + 8, y + 0.5);
  ctx.restore();
}
